import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonMenuButton,
  IonRouterLink,
  IonTitle,
  IonToolbar,
  NavController,
  ViewWillEnter,
} from '@ionic/angular/standalone';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import dayjs from 'dayjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  WorkCalculationitemsGroup,
  WorkCalculationitemsGroupsV2Service,
  WorkPermissionsEnum,
  WorkProject,
  WorkProjectsV2Service,
  WorkTask,
  WorkTasksV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { ProfileState } from '@work-frontend-monorepo/work-ui/state-management';
import { AccordionItemComponent } from '@work-frontend-monorepo/work-ui/wizard';
import { environment } from '@work-frontend-monorepo/work-environments';
import { BadgeComponent, ButtonComponent, IconComponent } from '@work-frontend-monorepo/work-ui/layout';
import { InfoblockComponent } from '@work-frontend-monorepo/work-ui/data-display';
import {
  formatDaterangeWithSameDayCheck,
  FormatNumberPipe,
  GetFullnameOfContactPipe,
  GetLabelVariantForStatusPipe, LinkifyPipe, NewlineToHtmlLinebreakPipe, PrependServerToFileSrcPipe, TelHrefPipe } from '@work-frontend-monorepo/work-ui/pipes';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';

/** Ein Montage-/Demontage-Task des Projekts samt Einsatzart-Label aus der Umgebung. */
interface ProjectAssignment {
  label: string;
  task: WorkTask;
}

@Component({
  selector: 'sfg-app-projekte-details-page',
  templateUrl: './projekte-details-page.component.html',
  styleUrls: ['./projekte-details-page.component.scss'],
  standalone: true,
  imports: [
    IonContent,
    ReactiveFormsModule,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonMenuButton,
    IonButtons,
    TranslatePipe,
    IonBackButton,
    ButtonComponent,
    IconComponent,
    InfoblockComponent,
    PrependServerToFileSrcPipe,
    RouterLink,
    BadgeComponent,
    GetLabelVariantForStatusPipe,
    IonRouterLink,
    TelHrefPipe,
    LinkifyPipe,
    NewlineToHtmlLinebreakPipe,
    GetFullnameOfContactPipe,
    formatDaterangeWithSameDayCheck,
    FormatNumberPipe,
    AccordionItemComponent,
  ],
})
export class ProjekteDetailsPageComponent implements ViewWillEnter {
  project = signal<WorkProject | undefined>(undefined);
  tasks = signal<WorkTask[]>([]);
  assignments = signal<ProjectAssignment[]>([]);
  /** Tag, von dem aus das Projekt geöffnet wurde (Montageplan/Dashboard) — bestimmt die hervorgehobene Einsatzart. */
  assignmentDate = signal<string | null>(null);
  activeAssignment = computed(() => {
    const assignmentDate = this.assignmentDate();
    if (!assignmentDate) {
      return undefined;
    }
    return this.assignments().find(
      (assignment) =>
        !dayjs(assignmentDate).isBefore(assignment.task.start, 'day') &&
        !dayjs(assignmentDate).isAfter(assignment.task.end ?? assignment.task.start, 'day'),
    );
  });
  private lastCheckedProjectId: number | null = null;

  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly workProjectsV2Service = inject(WorkProjectsV2Service);
  private readonly workTasksV2Service = inject(WorkTasksV2Service);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navController = inject(NavController);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly workCalculationitemsGroupsV2Service = inject(WorkCalculationitemsGroupsV2Service);

  private readonly hasCalculationPermission = toSignal(this.store.select(ProfileState.hasPermission(WorkPermissionsEnum.CalculationREAD)));
  private readonly profileRoles = toSignal(this.store.select(ProfileState.roles), { initialValue: [] });
  protected readonly canReadCalculation = computed(() => {
    if (!this.hasCalculationPermission()) {
      return false;
    }
    const requiredRoles = environment.appProjectCalculationRequiredRoles;
    return requiredRoles.length === 0 || requiredRoles.some((requiredRole) => this.profileRoles().includes(requiredRole));
  });
  protected calculationitemsGroups = signal<WorkCalculationitemsGroup[]>([]);

  private loadCalculationEffect = effect(async () => {
    const calculationId = this.project()?.calculation?.id;
    if (!calculationId || !this.canReadCalculation()) {
      this.calculationitemsGroups.set([]);
      return;
    }
    await this.loadCalculationitemsGroups(calculationId);
  });

  protected readonly environment = environment;
  private readonly ERSTINSTRUKTION_STANDARD_TASK_ID = 18;

  async ionViewWillEnter() {
    // Lade Tasks neu wenn die Seite wieder sichtbar wird (z.B. nach Navigation zurück)
    const projectId = this.activatedRoute.snapshot.params['projectId'];
    if (projectId) {
      const projectIdNumber = parseInt(projectId, 10);
      await this.loadProjectDetails(projectIdNumber);
      await this.loadTasks(projectIdNumber);
      this.assignmentDate.set(this.activatedRoute.snapshot.queryParams['date'] ?? null);
      await this.loadAssignments(projectIdNumber);

      // Prüfe Erstinstruktion nur wenn es ein neues Projekt ist
      // um Endlosschleifen zu vermeiden wenn User von Erstinstruktion zurücknavigiert
      const ignoreErstinstruktion = this.activatedRoute.snapshot.queryParams['ignoreErstinstruktion'] ?? false;
      if (this.lastCheckedProjectId !== projectIdNumber && !ignoreErstinstruktion) {
        await this.checkErstinstruktion(projectIdNumber);
      }
    }
  }

  protected openGoogleMaps() {
    const project = this.project();
    let street = project?.objectStreet;
    let zip = project?.objectZip;
    let city = project?.objectCity;

    if (!street) {
      street = project?.container?.contact?.street;
    }

    const address = encodeURIComponent(`${street}, ${zip} ${city}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_system');
  }

  private async loadProjectDetails(projectId: number) {
    try {
      const projectResponse = await lastValueFrom(
        this.workProjectsV2Service.projectsV2ControllerFindOneById({
          id: projectId,
          withAllDetails: true,
        }),
      );
      this.project.set(projectResponse);
    } catch (loadProjectException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProjectException', loadProjectException);
    }
  }

  private async loadTasks(projectId: number) {
    try {
      const standardTaskIds = environment.standardTaskIdsToShowInAppDashboard;

      if (standardTaskIds.length === 0) {
        return;
      }

      const tasksResponse = await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerFindAllTasksForProjectsAndStandardtasks({
          body: {
            projectIds: [projectId],
            standardTaskIds: standardTaskIds,
          },
        }),
      );

      this.tasks.set(tasksResponse);
    } catch (loadTasksException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadTasksException', loadTasksException);
    }
  }

  private async loadAssignments(projectId: number) {
    try {
      const assignmentTypes = environment.appProjectAssignmentTypes;
      if (assignmentTypes.length === 0) {
        return;
      }

      const tasksResponse = await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerFindAllTasksForProjectsAndStandardtasks({
          body: {
            projectIds: [projectId],
            standardTaskIds: assignmentTypes.flatMap((assignmentType) => assignmentType.standardtaskIds),
          },
        }),
      );

      const assignments: ProjectAssignment[] = tasksResponse
        .map((task) => ({
          label: assignmentTypes.find((assignmentType) => assignmentType.standardtaskIds.includes(task.standardtaskId ?? 0))?.label ?? '',
          task,
        }))
        .sort((assignmentA, assignmentB) => dayjs(assignmentA.task.start).valueOf() - dayjs(assignmentB.task.start).valueOf());
      this.assignments.set(assignments);
    } catch (loadAssignmentsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadAssignmentsException', loadAssignmentsException);
    }
  }

  private async loadCalculationitemsGroups(calculationId: number) {
    try {
      const groupsResponse = await lastValueFrom(
        this.workCalculationitemsGroupsV2Service.calculationitemsGroupsV2ControllerFindAllByCalculationId({ id: calculationId }),
      );
      this.calculationitemsGroups.set(groupsResponse);
    } catch (loadCalculationitemsGroupsException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadCalculationitemsGroupsException', loadCalculationitemsGroupsException);
    }
  }

  private async checkErstinstruktion(projectId: number) {
    try {
      // Suche nach Erstinstruktion-Task
      const tasksResponse = await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerFindAllTasksForProjectsAndStandardtasks({
          body: {
            projectIds: [projectId],
            standardTaskIds: [this.ERSTINSTRUKTION_STANDARD_TASK_ID],
          },
        }),
      );

      // Markiere dieses Projekt als geprüft
      this.lastCheckedProjectId = projectId;

      // Kein Task vorhanden
      if (!tasksResponse || tasksResponse.length === 0) {
        const project = this.project();
        const kundenberaterName =
          project?.user?.firstName && project?.user?.lastName ? `${project.user.firstName} ${project.user.lastName}` : null;

        this.alertDialogService.showAlert({
          title: 'ERSTINSTRUKTION_FEHLT',
          message: kundenberaterName
            ? this.translateService.instant('ERSTINSTRUKTION_FEHLT_HINWEIS_MIT_NAME', { name: kundenberaterName })
            : 'ERSTINSTRUKTION_FEHLT_HINWEIS',
          feedbackState: 'warning',
        });

        // Navigiere zurück zur Projektliste
        await this.navController.navigateBack(['/mitarbeitende/projekte']);
        return;
      }

      const erstinstruktionTask = tasksResponse[0];

      // Task ist nicht erledigt -> Hinweis anzeigen und zur Erstinstruktion weiterleiten
      if (erstinstruktionTask.status !== 'Erledigt') {
        await this.navController.navigateForward(['/mitarbeitende/projekte/details', projectId, 'erstinstruktion'], {
          animated: true,
        });

        this.alertDialogService.showAlert({
          title: 'ERSTINSTRUKTION_ERFORDERLICH',
          message: 'ERSTINSTRUKTION_ERFORDERLICH_HINWEIS',
          feedbackState: 'warning',
        });
      }
    } catch (checkErstinstruktionException) {
      this.exceptionHandlingService.handleExceptionWithAlert('checkErstinstruktionException', checkErstinstruktionException);
    }
  }
}
